/* Discount card — the Build-Your-Bundle explainer, above the bottom rail (Sep 2026). One of the rail cards:
   placement, open/close, triggers and analytics come from assets/rail-card.js; markup, styling and copy are in
   sections/bottom-rail.liquid (<discount-card>, the .rail-card__*, .basket__* and .ladder__* rules, the "Ladder
   card" settings). It replaces sections/discount-explainer-modal.liquid + assets/discount-promotion.js: a
   centred, scroll-locked modal whose animation added one product per rung (so eight thumbnails read "40% off"
   when the real ladder needs 25 items), never showed a threshold, and looped.

   What the discount does is lower the price of everything already in the cart each time something is added,
   so the card shows that on the shopper's own things: a basket of real products, each price struck and
   re-stated at the cart's rung, then the next step as the row that is not there yet ("Add one more and save
   10%. That's $4.20."). The tiles arrive one after another and each price ticks down once — the build-up — and
   the card redraws when the cart changes under it. Opened only by its triggers; never by itself.

   Whose things, in order: the cart's eligible lines (a <template> the section renders), the wishlist
   (assets/wishlist.js ids and handles, resolved here through /products/<handle>.js), the product on the page,
   staff picks. Eligible means what Shopify's automatic discounts count: paid, no compare-at, not free. */

(() => {
  'use strict';

  if (!window.RailCard) return;

  const { RailCard, REDUCED_MOTION } = window.RailCard;

  const MAX_ROWS = 4; // the grid is four blocks: up to three tiles and the slot, or four tiles and no slot (Anthony, 2026-09-22)
  const WISHLIST_FETCH_TIMEOUT_MS = 4000;
  const ROW_STAGGER_MS = 90; // matches the animation-delay step in the section's styles
  const TICK_MS = 520; // one price counting down
  const NEXT_EXTRA_DELAY_MS = 120;

  function money(cents) {
    if (typeof window.formatShopMoney === 'function') {
      try {
        return window.formatShopMoney(Math.round(cents));
      } catch {
        /* fall through */
      }
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  /* "2:5,4:10" → [{threshold: 2, percent: 5}, ...], sorted, malformed pairs dropped */
  function parseLadder(raw) {
    return String(raw || '')
      .split(',')
      .map((pair) => pair.trim().split(':'))
      .map(([t, p]) => ({ threshold: Number(t), percent: Number(p) }))
      .filter((r) => Number.isFinite(r.threshold) && Number.isFinite(r.percent) && r.threshold > 0)
      .sort((a, b) => a.threshold - b.threshold);
  }

  function rungFor(rungs, count) {
    let best = null;
    for (const rung of rungs) {
      if (count >= rung.threshold && (!best || rung.percent > best.percent)) best = rung;
    }
    return best;
  }

  function nextRung(rungs, count) {
    return rungs.find((rung) => rung.threshold > count) || null;
  }

  function easeOut(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  /* One basket tile, the same markup the section's templates carry. */
  function buildRow(row) {
    const li = document.createElement('div');
    li.className = 'basket__tile';
    li.dataset.price = String(Math.round(row.price));
    li.dataset.qty = String(row.qty || 1);
    const media = document.createElement('span');
    media.className = 'basket__media';
    media.setAttribute('data-basket-media', '');
    if (row.image) {
      const img = document.createElement('img');
      img.className = 'basket__img';
      img.src = row.image;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 180;
      img.height = 180;
      media.appendChild(img);
    }
    li.appendChild(media);
    const title = document.createElement('span');
    title.className = 'basket__title';
    title.textContent = row.title || '';
    if ((row.qty || 1) > 1) {
      const qty = document.createElement('span');
      qty.className = 'basket__qty';
      qty.textContent = ` × ${row.qty}`;
      title.appendChild(qty);
    }
    li.appendChild(title);
    const price = document.createElement('span');
    price.className = 'basket__price';
    price.innerHTML = '<s class="basket__full" data-basket-full hidden></s><span class="basket__now" data-basket-now></span>';
    li.appendChild(price);
    return li;
  }

  /* The media box shimmers until its image has loaded (or when there is none, until told it never will). */
  function watchMedia(tile) {
    tile.querySelectorAll('[data-basket-media]').forEach((media) => {
      const img = media.querySelector('img');
      if (!img) {
        media.classList.add('is-loaded');
        return;
      }
      const done = () => media.classList.add('is-loaded');
      if (img.complete && img.naturalWidth > 0) {
        done();
      } else {
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    });
  }

  function buildSkeleton() {
    const li = document.createElement('div');
    li.className = 'basket__tile basket__tile--skeleton';
    li.setAttribute('aria-hidden', 'true');
    li.innerHTML = '<span class="basket__media"></span><span class="basket__bar"></span><span class="basket__bar"></span>';
    return li;
  }

  class DiscountCard extends RailCard {
    triggerActions() {
      /* open-discount-modal is the retired modal's hook, still on the (disabled) announcement bars */
      return ['open-discount-card', 'open-discount-modal'];
    }

    eventName() {
      return 'Ladder Card';
    }

    openProps() {
      return { cart_items: this.cartCount, source: this.source || 'none' };
    }

    onWire() {
      this.rungs = parseLadder(this.getAttribute('data-ladder'));
      this.basketEl = this.querySelector('[data-basket]');
      this.nextEl = this.querySelector('[data-basket-next]');
      this.nextTextEl = this.querySelector('[data-basket-next-text]');
      this.nextMoneyEl = this.querySelector('[data-basket-next-money]');
      this.leadEl = this.querySelector('[data-ladder-lead]');
      this.cartCount = Number(this.getAttribute('data-ladder-cart-count') || 0);
      this.cartSubtotal = Number(this.getAttribute('data-ladder-cart-subtotal') || 0);
      this.source = null;
      this.drawn = false;
      this.tickers = [];

      /* The cart changed under an open card (the drawer, quick buy): rebuild from the live cart. */
      this._onCartUpdated = (event) => {
        if (!this.isOpen) return;
        this.fromLiveCart(event && event.detail ? event.detail.cart : null);
      };
      document.documentElement.addEventListener('cart:updated', this._onCartUpdated);
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      document.documentElement.removeEventListener('cart:updated', this._onCartUpdated);
      this.stopTickers();
    }

    onOpen() {
      /* Rows are chosen once per open, so the draw is what the shopper sees arrive. Skeletons hold the grid's
         height until they do (the wishlist is a fetch), so the card never opens short and then grows. */
      this.skeletons();
      this.draw();
    }

    onClose() {
      this.stopTickers();
    }

    /* ---------- the rows ---------- */

    template(name) {
      const tpl = this.querySelector(`template[data-basket-source="${name}"]`);
      if (!tpl) return [];
      return Array.from(tpl.content.querySelectorAll('.basket__tile')).map((li) => li.cloneNode(true));
    }

    /* Cart rows, else wishlist rows, else the product, else staff picks. The wishlist is asynchronous; the
       others are already in the DOM. */
    async chooseRows() {
      const cartRows = this.template('cart');
      if (cartRows.length) return { source: 'cart', rows: cartRows, hypothetical: false };

      const wishlistRows = await this.wishlistRows();
      if (wishlistRows.length) return { source: 'wishlist', rows: wishlistRows, hypothetical: true };

      const productRows = this.template('product');
      if (productRows.length) return { source: 'product', rows: productRows, hypothetical: true };

      const pickRows = this.template('picks');
      if (pickRows.length) return { source: 'picks', rows: pickRows, hypothetical: true };

      return { source: 'none', rows: [], hypothetical: true };
    }

    async wishlistRows() {
      if (!window.Wishlist || typeof window.Wishlist.getItems !== 'function') return [];
      let items = [];
      try {
        items = window.Wishlist.getItems() || [];
      } catch {
        return [];
      }
      const handles = items.map((item) => item && (item.handle || item.product_handle)).filter(Boolean).slice(0, MAX_ROWS + 2);
      if (!handles.length) return [];
      const settled = await Promise.all(handles.map((handle) => this.productJson(handle)));
      return settled
        .filter((p) => p && p.available !== false && Number(p.price) > 0 && !p.compare_at_price)
        .slice(0, MAX_ROWS)
        .map((p) =>
          buildRow({
            price: Number(p.price),
            qty: 1,
            title: p.title,
            image: p.featured_image ? p.featured_image.replace(/(\.[a-z]+)(\?.*)?$/i, '_360x$1$2') : '',
          })
        );
    }

    async productJson(handle) {
      try {
        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        const timer = controller ? window.setTimeout(() => controller.abort(), WISHLIST_FETCH_TIMEOUT_MS) : 0;
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' },
          signal: controller ? controller.signal : undefined,
        });
        window.clearTimeout(timer);
        if (!response.ok) return null;
        return await response.json();
      } catch {
        return null;
      }
    }

    /* After a cart change: rows from cart.js (which cannot see compare-at, so sale lines may show here; the
       count comes from the drawer's own eligible count where it exists), then redraw. */
    async fromLiveCart(cart) {
      let json = cart;
      if (!json) {
        try {
          const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          json = await response.json();
        } catch {
          return;
        }
      }
      const lines = (json.items || []).filter((item) => item.price > 0);
      this.cartCount = this.eligibleCount(json, lines);
      this.cartSubtotal = lines.reduce((sum, item) => sum + item.original_line_price, 0);
      const rows = lines.slice(0, MAX_ROWS).map((item) =>
        buildRow({
          price: item.original_line_price,
          qty: item.quantity,
          title: item.product_title,
          image: item.image ? item.image.replace(/(\.[a-z]+)(\?.*)?$/i, '_360x$1$2') : '',
        })
      );
      if (rows.length) {
        this.render({ source: 'cart', rows, hypothetical: false });
      } else {
        this.draw(); // the cart emptied: back to the wishlist / product / picks
      }
    }

    eligibleCount(cart, lines) {
      if (typeof window.getCartEligibleCount === 'function') {
        try {
          const n = window.getCartEligibleCount(cart);
          if (Number.isFinite(n)) return n;
        } catch {
          /* fall through */
        }
      }
      return lines.reduce((sum, item) => sum + item.quantity, 0);
    }

    skeletons() {
      if (!this.basketEl) return;
      this.clearTiles();
      for (let i = 0; i < MAX_ROWS; i += 1) this.placeTile(buildSkeleton());
      this.schedulePosition();
    }

    clearTiles() {
      Array.from(this.basketEl.querySelectorAll('.basket__tile:not(.basket__next)')).forEach((el) => el.remove());
    }

    placeTile(el) {
      if (this.nextEl && this.nextEl.parentNode === this.basketEl) {
        this.basketEl.insertBefore(el, this.nextEl);
      } else {
        this.basketEl.appendChild(el);
      }
    }

    async draw() {
      const chosen = await this.chooseRows();
      if (!this.isOpen && this.drawn) return; // closed while the wishlist resolved
      this.render(chosen);
    }

    /* ---------- the draw ---------- */

    render(chosen) {
      this.stopTickers();
      this.source = chosen.source;
      this.drawn = true;
      const rungs = this.rungs;
      const rows = chosen.rows;

      /* Cart: the count and subtotal are the whole cart's, even when only four rows show. Hypothetical baskets
         (wishlist, product, picks): the rows are the basket. */
      const count = chosen.hypothetical ? rows.reduce((sum, li) => sum + Number(li.dataset.qty || 1), 0) : this.cartCount;
      const subtotal = chosen.hypothetical ? rows.reduce((sum, li) => sum + Number(li.dataset.price || 0), 0) : this.cartSubtotal;
      const current = rungFor(rungs, count);
      const next = nextRung(rungs, count);
      const pct = current ? current.percent : 0;
      const still = REDUCED_MOTION.matches;

      if (this.basketEl) {
        /* Only the tiles (and skeletons) go; the slot stays last in the grid, and the tiles go in front of it. */
        this.clearTiles();
        rows.forEach((li, index) => {
          li.style.setProperty('--i', String(index));
          if (!still) li.setAttribute('data-enter', '');
          const full = Number(li.dataset.price || 0);
          const now = full * (1 - pct / 100);
          const fullEl = li.querySelector('[data-basket-full]');
          const nowEl = li.querySelector('[data-basket-now]');
          if (fullEl) {
            fullEl.textContent = money(full);
            fullEl.hidden = pct === 0;
          }
          if (nowEl) {
            if (still || pct === 0) {
              nowEl.textContent = money(now);
            } else {
              nowEl.textContent = money(full);
              this.tick(nowEl, full, now, index * ROW_STAGGER_MS + 150);
            }
          }
          watchMedia(li);
          this.placeTile(li);
        });
      }

      if (this.leadEl) {
        const leadCart = this.getAttribute('data-copy-lead-cart') || '';
        this.leadEl.textContent =
          !chosen.hypothetical && current && leadCart
            ? this.fill(leadCart, { n: count, pct })
            : this.getAttribute('data-copy-lead') || '';
      }

      if (this.nextEl && this.nextTextEl) {
        const { text, saving } = this.nextLine(count, subtotal, current, next);
        this.nextTextEl.textContent = text;
        if (this.nextMoneyEl) {
          this.nextMoneyEl.textContent = saving;
          this.nextMoneyEl.hidden = !saving;
        }
        /* Four blocks, always: with four tiles there is no room for the slot, and at four the rung is the top
           one anyway — the band's lead carries the "4 items, 10% off". */
        this.nextEl.hidden = !text || rows.length >= MAX_ROWS;
        if (text && !still) {
          this.nextEl.removeAttribute('data-enter');
          this.nextEl.style.setProperty('--i', String(rows.length + NEXT_EXTRA_DELAY_MS / ROW_STAGGER_MS));
          this.nextEl.clientWidth; // restart the entrance
          this.nextEl.setAttribute('data-enter', '');
        }
      }

      this.schedulePosition(); // the height changed; the bottom edge must not
    }

    /* "Add one more, save 10%" over "$4.20 off" — the saving is the next percent of what is here. */
    nextLine(count, subtotal, current, next) {
      const savingLine = (pct) => (subtotal > 0 ? this.fill(this.getAttribute('data-copy-money') || '', { money: money(subtotal * (pct / 100)) }) : '');
      if (next) {
        const togo = next.threshold - count;
        if (subtotal <= 0) {
          return { text: this.fill(this.getAttribute('data-copy-empty') || '', { n: next.threshold, pct: next.percent }), saving: '' };
        }
        const template = this.getAttribute(togo === 1 ? 'data-copy-next-one' : 'data-copy-next-many') || '';
        return { text: this.fill(template, { n: togo, pct: next.percent }), saving: savingLine(next.percent) };
      }
      if (current) {
        return { text: this.fill(this.getAttribute('data-copy-top') || '', { pct: current.percent }), saving: savingLine(current.percent) };
      }
      return { text: '', saving: '' };
    }

    fill(template, values) {
      let text = template;
      Object.keys(values).forEach((name) => {
        text = text.split(`[${name}]`).join(String(values[name]));
      });
      return text;
    }

    /* A price counting down from full to discounted, once, after its row has arrived. */
    tick(el, from, to, delayMs) {
      const start = performance.now() + delayMs;
      const state = { raf: 0 };
      const step = (now) => {
        const t = Math.min(1, Math.max(0, (now - start) / TICK_MS));
        el.textContent = money(from + (to - from) * easeOut(t));
        if (t < 1) state.raf = window.requestAnimationFrame(step);
      };
      state.raf = window.requestAnimationFrame(step);
      this.tickers.push(state);
    }

    stopTickers() {
      this.tickers.forEach((state) => window.cancelAnimationFrame(state.raf));
      this.tickers = [];
    }
  }

  if (!window.customElements.get('discount-card')) {
    window.customElements.define('discount-card', DiscountCard);
  }

  window.DiscountCard = {
    open(by) {
      const el = document.querySelector('discount-card');
      if (el && typeof el.open === 'function') el.open(by || 'api');
    },
    close() {
      const el = document.querySelector('discount-card');
      if (el && typeof el.close === 'function') el.close('api');
    },
  };
})();
